<script>
import { ref } from 'vue';
import axios from 'axios';
import { resolveAceleresApiBase } from 'shared/helpers/aceleresApiBase';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import ContactAPI from 'dashboard/api/contacts';

export default {
  components: { Avatar },
  props: {
    group: {
      type: Object,
      default: () => ({}),
    },
    inboxId: {
      type: [Number, String],
      default: null,
    },
    conversationId: {
      type: [Number, String],
      default: null,
    },
    accessToken: {
      type: String,
      default: '',
    },
  },
  emits: ['groupUpdated'],
  setup() {
    const showAddContactsModal = ref(false);
    const addContactsQuery = ref('');
    const addContacts = ref([]);
    const selectedContactIds = ref([]);
    const addContactsLoading = ref(false);
    const addContactsSaving = ref(false);
    const addContactsPage = ref(1);
    const addContactsHasMore = ref(false);
    const addContactsError = ref('');
    const showSearchModal = ref(false);
    const showParticipantModal = ref(false);
    const showGroupToolsModal = ref(false);
    const searchQuery = ref('');
    const selectedParticipant = ref(null);
    const showParticipantDetails = ref(false);
    const isOpeningParticipant = ref(false);
    const participantDetailLabels = {
      name: 'Nome',
      phone: 'Telefone',
      role: 'Papel no grupo',
      member: 'Participante',
      noPhone: 'Não disponibilizado pelo WhatsApp.',
      noRegistration:
        'Este participante não tem um cadastro disponível no Zoraxy.',
    };

    return {
      showAddContactsModal,
      addContactsQuery,
      addContacts,
      selectedContactIds,
      addContactsLoading,
      addContactsSaving,
      addContactsPage,
      addContactsHasMore,
      addContactsError,
      showSearchModal,
      showParticipantModal,
      showGroupToolsModal,
      searchQuery,
      selectedParticipant,
      showParticipantDetails,
      isOpeningParticipant,
      participantDetailLabels,
    };
  },
  computed: {
    participants() {
      return Array.isArray(this.group.participants)
        ? this.group.participants
        : [];
    },
    participantCount() {
      return Number(this.group.participants_count || this.participants.length);
    },
    participantCountLabel() {
      return `${this.participantCount} membros`;
    },
    searchMembersLabel() {
      return 'Pesquisar membros';
    },
    emptyMembersLabel() {
      return 'Nenhum membro recebido para este grupo.';
    },
    closeModalLabel() {
      return 'Fechar pesquisa de membros';
    },
    searchContactsPlaceholder() {
      return 'Pesquisar contatos';
    },
    noMembersFoundLabel() {
      return 'Nenhum membro encontrado.';
    },
    promoteAdminLabel() {
      return 'Promover a admin do grupo';
    },
    removeMemberLabel() {
      return 'Remover';
    },
    demoteAdminLabel() {
      return 'Remover como admin';
    },
    addMemberLabel() {
      return 'Adicionar membro';
    },
    groupToolsLabel() {
      return 'Configurações do grupo';
    },
    contactDetailsLabel() {
      return 'Dados do contato';
    },
    filteredParticipants() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.participants;

      return this.participants.filter(participant => {
        const name = this.participantName(participant).toLowerCase();
        const phone = this.participantPhone(participant).toLowerCase();
        return name.includes(query) || phone.includes(query);
      });
    },
    selectedParticipantName() {
      return this.selectedParticipant
        ? this.participantName(this.selectedParticipant)
        : '';
    },
    selectedParticipantPhone() {
      const phone = String(this.selectedParticipant?.phone_number || '').replace(
        /\D/g,
        ''
      );
      return /^\d{8,15}$/.test(phone) ? `+${phone}` : '';
    },
    canManageGroup() {
      return (
        this.group.current_user_is_admin === true ||
        this.group.current_user_is_admin === 'true'
      );
    },
    selectedParticipantIsAdmin() {
      return Boolean(
        this.selectedParticipant &&
          (this.selectedParticipant.is_admin ||
            this.selectedParticipant.admin ||
            ['owner', 'admin'].includes(this.selectedParticipant.role))
      );
    },
    canManageSelectedParticipant() {
      return this.canManageGroup && !this.selectedParticipant?.is_me;
    },
  },
  methods: {
    participantKey(participant, index) {
      if (typeof participant === 'string') return participant;
      return (
        participant.id ||
        participant.phone_number ||
        `${participant.name || 'member'}-${index}`
      );
    },
    participantName(participant) {
      if (typeof participant === 'string')
        return participant.replace(/@.*/, '');
      return (
        participant.name ||
        (participant.phone_number ? `+${participant.phone_number}` : '') ||
        (participant.id || '').replace(/@.*/, '')
      );
    },
    participantAvatar(participant) {
      if (typeof participant === 'string') return '';
      return (
        participant.thumbnail ||
        participant.avatar_url ||
        participant.profile_pic_url ||
        participant.picture ||
        participant.image_url ||
        ''
      );
    },
    participantPhone(participant) {
      if (typeof participant === 'string' || !participant.phone_number)
        return '';
      return `+${participant.phone_number}`;
    },
    participantRoleBadge(participant) {
      if (typeof participant === 'string') return '';
      if (participant.is_owner || participant.role === 'owner')
        return 'Admin do grupo';
      if (
        participant.is_admin ||
        participant.admin ||
        participant.role === 'admin'
      ) {
        return 'Admin do grupo';
      }
      return '';
    },
    participantSecondaryText(participant) {
      if (typeof participant === 'string') return '';
      return (
        participant.about ||
        participant.description ||
        participant.status ||
        this.participantPhone(participant)
      );
    },
    openSearchModal() {
      this.searchQuery = '';
      this.selectedParticipant = null;
      this.showSearchModal = true;
    },
    closeSearchModal() {
      this.showSearchModal = false;
    },
    openParticipantActions(participant) {
      this.showParticipantDetails = false;
      this.selectedParticipant = participant;
      this.showSearchModal = false;
      this.showParticipantModal = true;
    },
    closeParticipantActions() {
      this.showParticipantDetails = false;
      this.selectedParticipant = null;
      this.showParticipantModal = false;
    },
    async openSelectedParticipantDetails() {
      const participant = this.selectedParticipant;
      if (!participant || this.isOpeningParticipant) return;
      this.isOpeningParticipant = true;
      try {
        let contactId = participant.contact_id;
        if (!contactId && this.selectedParticipantPhone) {
          const phone = this.selectedParticipantPhone.slice(1);
          const { data } = await ContactAPI.search(phone);
          const contact = (data.payload || []).find(
            item =>
              String(item.phone_number || item.phoneNumber || '').replace(
                /\D/g,
                ''
              ) === phone
          );
          contactId = contact?.id;
        }
        if (
          this.selectedParticipant !== participant ||
          !this.showParticipantModal
        )
          return;
        if (contactId) {
          await this.$router.push({
            name: 'contacts_edit',
            params: { accountId: this.$route.params.accountId, contactId },
          });
          this.closeParticipantActions();
        } else {
          this.showParticipantDetails = true;
        }
      } catch {
        useAlert(
          'Não foi possível carregar os dados deste participante. Tente novamente.'
        );
      } finally {
        this.isOpeningParticipant = false;
      }
    },
    async startSelectedParticipantConversation() {
      if (!this.selectedParticipant || this.isOpeningParticipant) return;
      const phone = this.selectedParticipantPhone;
      if (!phone) {
        useAlert(
          'O WhatsApp não disponibilizou um telefone válido para este participante.'
        );
        return;
      }
      this.isOpeningParticipant = true;
      try {
        await this.$router.push({
          name: 'home',
          params: { accountId: this.$route.params.accountId },
          query: { start_phone: phone },
        });
        this.closeParticipantActions();
      } catch {
        useAlert(
          'Não foi possível abrir a conversa com este participante. Tente novamente.'
        );
      } finally {
        this.isOpeningParticipant = false;
      }
    },
    groupApiUrl() {
      return `${resolveAceleresApiBase()}/api/wame/groups/${this.inboxId}`;
    },
    groupApiHeaders() {
      return {
        'X-Chatwoot-Token': this.accessToken,
        'X-Chatwoot-Account-Id': this.$route.params.accountId || '',
      };
    },
    participantIdentifier(participant) {
      return participant?.id || participant?.phone_number || '';
    },
    async updateParticipants(action, participants) {
      if (!this.group.jid || !this.inboxId) return;
      try {
        const response = await axios.post(
          `${this.groupApiUrl()}/participants`,
          { group_jid: this.group.jid, action, participants },
          { headers: this.groupApiHeaders() }
        );
        this.$emit('groupUpdated', response.data.group);
        useAlert('Grupo atualizado no WhatsApp.');
      } catch (error) {
        useAlert(
          error.response?.data?.message ||
            'Não foi possível atualizar os participantes do grupo.'
        );
      }
    },
    async updateSelectedParticipant(action) {
      const identifier = this.participantIdentifier(this.selectedParticipant);
      if (!identifier) return;
      if (!this.conversationId || this.isOpeningParticipant) return;
      this.isOpeningParticipant = true;
      try {
        const accountId = this.$route.params.accountId;
        const response = await window.axios.post(
          `/api/v1/accounts/${accountId}/aceleres/conversations/${this.conversationId}/group_participant_actions`,
          { action, participant_id: identifier }
        );
        this.$emit('groupUpdated', response.data.group);
        useAlert('Grupo atualizado no WhatsApp.');
        this.closeParticipantActions();
      } catch (error) {
        useAlert(
          error.response?.data?.message ||
            'Não foi possível atualizar os participantes do grupo.'
        );
      } finally {
        this.isOpeningParticipant = false;
      }
    },
    closeAddContacts() {
      if (!this.addContactsSaving) this.showAddContactsModal = false;
    },
    async addParticipant() {
      this.addContactsQuery = '';
      this.selectedContactIds = [];
      this.addContacts = [];
      this.showAddContactsModal = true;
      await this.loadAddContacts();
    },
    contactJoinUnavailableReason(contact) {
      const phone = String(contact.phone_number || '').replace(/\D/g, '');
      return /^\d{8,15}$/.test(phone) ? 'Já participa' : 'Sem telefone válido';
    },
    contactCanJoin(contact) {
      const phone = String(contact.phone_number || '').replace(/\D/g, '');
      return (
        /^\d{8,15}$/.test(phone) &&
        !this.participants.some(
          member => String(member.phone_number || '').replace(/\D/g, '') === phone
        )
      );
    },
    async loadAddContacts(nextPage = 1) {
      if (this.addContactsLoading || this.addContactsSaving) return;
      this.addContactsLoading = true;
      this.addContactsError = '';
      try {
        const query = this.addContactsQuery.trim();
        const { data } = await window.axios.get(
          `/api/v1/accounts/${this.$route.params.accountId}/contacts${query ? '/search' : ''}`,
          {
            params: {
              page: nextPage,
              sort: 'name',
              include_contact_inboxes: false,
              ...(query ? { q: query } : {}),
            },
          }
        );
        const rows = Array.isArray(data.payload) ? data.payload : [];
        this.addContacts =
          nextPage === 1
            ? rows
            : [
                ...this.addContacts,
                ...rows.filter(
                  row => !this.addContacts.some(old => old.id === row.id)
                ),
              ];
        this.addContactsPage = nextPage;
        this.addContactsHasMore = rows.length >= 15;
      } catch {
        this.addContactsError = 'Não foi possível carregar os contatos. Tente novamente.';
      } finally {
        this.addContactsLoading = false;
      }
    },
    async confirmAddContacts() {
      if (
        !this.selectedContactIds.length ||
        this.addContactsSaving ||
        !this.conversationId
      ) return;
      this.addContactsSaving = true;
      this.addContactsError = '';
      try {
        const { data } = await window.axios.post(
          `/api/v1/accounts/${this.$route.params.accountId}/aceleres/conversations/${this.conversationId}/group_participant_actions`,
          { action: 'add', contact_ids: this.selectedContactIds }
        );
        this.$emit('groupUpdated', data.group);
        this.showAddContactsModal = false;
        useAlert('Contatos adicionados ao grupo.');
      } catch (error) {
        this.addContactsError =
          error.response?.data?.message ||
          'Não foi possível adicionar os contatos ao grupo.';
        await this.refreshGroup();
      } finally {
        this.addContactsSaving = false;
      }
    },
    async refreshGroup() {
      if (!this.group.jid || !this.inboxId) return;
      try {
        const response = await axios.get(this.groupApiUrl(), {
          headers: this.groupApiHeaders(),
          params: { group_jid: this.group.jid },
        });
        this.$emit('groupUpdated', response.data.group);
      } catch {
        // A cache já exibida continua útil se a consulta remota oscilar.
      }
    },
    async updateGroupSetting(setting) {
      if (!this.group.jid || !this.inboxId) return;
      try {
        const response = await axios.patch(
          `${this.groupApiUrl()}/settings`,
          { group_jid: this.group.jid, setting },
          { headers: this.groupApiHeaders() }
        );
        this.$emit('groupUpdated', response.data.group);
        useAlert('Configuração do grupo atualizada no WhatsApp.');
      } catch (error) {
        useAlert(
          error.response?.data?.message ||
            'Não foi possível atualizar a configuração do grupo.'
        );
      }
    },
    async copyInviteLink() {
      try {
        const response = await axios.get(`${this.groupApiUrl()}/invite`, {
          headers: this.groupApiHeaders(),
          params: { group_jid: this.group.jid },
        });
        const invite = response.data.invite || {};
        const code = invite.code || invite.inviteCode || invite.data?.code;
        const link =
          invite.link ||
          invite.inviteLink ||
          invite.data?.link ||
          (code ? `https://chat.whatsapp.com/${code}` : '');
        if (!link) throw new Error('Link não recebido');
        await navigator.clipboard.writeText(link);
        useAlert('Link de convite copiado.');
      } catch (error) {
        useAlert(
          error.response?.data?.message ||
            'Não foi possível obter o link de convite do grupo.'
        );
      }
    },
  },
  mounted() {
    this.refreshGroup();
  },
};
</script>

<template>
  <section
    id="contact-group-members"
    class="contact-details-group"
    aria-labelledby="contact-group-members-title"
  >
    <header class="contact-details-group__header">
      <h3 id="contact-group-members-title" class="contact-details-group__title">
        {{ participantCountLabel }}
      </h3>
      <div class="flex items-center gap-1">
        <button
          v-if="canManageGroup"
          type="button"
          class="contact-details-group__search !hidden"
          aria-label="Adicionar contatos"
          title="Adicionar contatos"
          @click="addParticipant"
        >
          <span class="i-lucide-user-plus" aria-hidden="true" />
        </button>
        <!--
        <button
          v-if="canManageGroup"
          type="button"
          class="contact-details-group__search"
          :aria-label="groupToolsLabel"
          @click="showGroupToolsModal = true"
        >
          <span class="i-lucide-settings-2" aria-hidden="true" />
        </button>
        -->
        <button
          type="button"
          class="contact-details-group__search"
          :aria-label="searchMembersLabel"
          @click="openSearchModal"
        >
          <span class="i-lucide-search" aria-hidden="true" />
        </button>
      </div>
    </header>

    <div class="contact-details-group__content">
      <div v-if="participants.length" class="contact-details-group__members">
        <div
          v-for="(participant, index) in participants"
          :key="participantKey(participant, index)"
          class="contact-details-group__member hover:rounded-lg hover:bg-n-slate-2 hover:outline hover:outline-1 hover:outline-n-weak hover:cursor-grab"
          @click="openSearchModal"
          @contextmenu.prevent.stop="openParticipantActions(participant)"
        >
          <Avatar
            :src="participantAvatar(participant)"
            :name="participantName(participant)"
            :size="52"
            rounded-full
            hide-offline-status
            class="contact-details-group__member-avatar"
          />
          <div class="contact-details-group__member-text">
            <span class="contact-details-group__member-name">{{
              participantName(participant)
            }}</span>
            <span
              v-if="participantSecondaryText(participant)"
              class="contact-details-group__member-phone"
            >
              {{ participantSecondaryText(participant) }}
            </span>
          </div>
          <span
            v-if="participantRoleBadge(participant)"
            class="contact-details-group__member-role"
          >
            {{ participantRoleBadge(participant) }}
          </span>
        </div>
      </div>
      <p v-else class="contact-details-group__empty">
        {{ emptyMembersLabel }}
      </p>
    </div>

    <woot-modal
      :show="showAddContactsModal"
      :on-close="closeAddContacts"
      :show-close-button="false"
      :close-on-backdrop-click="!addContactsSaving"
      container-class="!w-[40rem] !max-w-[calc(100vw-2rem)] !overflow-hidden !rounded-2xl"
    >
      <section class="flex max-h-[85dvh] flex-col bg-n-solid-1" aria-labelledby="group-add-contacts-title">
        <header class="flex items-start gap-4 border-b border-n-weak px-6 py-5">
          <div class="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-n-brand/10 text-n-brand">
            <span class="i-lucide-user-round-plus size-6" aria-hidden="true" />
          </div>
          <div class="min-w-0 flex-1">
            <h3 id="group-add-contacts-title" class="!m-0 text-lg font-semibold text-n-slate-12">Adicionar contatos</h3>
            <p class="!mb-0 !mt-1 truncate text-sm text-n-slate-11">{{ group.subject || 'Grupo do WhatsApp' }}</p>
          </div>
          <button type="button" aria-label="Fechar" :disabled="addContactsSaving" class="flex size-8 shrink-0 items-center justify-center rounded-lg text-n-slate-10 transition-colors hover:bg-n-slate-3 hover:text-n-slate-12 disabled:opacity-40" @click="closeAddContacts">
            <span class="i-lucide-x size-5" aria-hidden="true" />
          </button>
        </header>

        <div class="flex min-h-0 flex-col gap-4 px-6 py-5">
          <form class="!m-0 flex !self-stretch gap-2 !p-0" @submit.prevent="loadAddContacts()">
            <div class="relative min-w-0 flex-1">
              <span class="i-lucide-search pointer-events-none absolute left-3.5 top-3.5 size-4 text-n-slate-10" aria-hidden="true" />
              <input
                v-model="addContactsQuery"
                aria-label="Buscar contatos por nome ou telefone"
                placeholder="Buscar por nome ou telefone…"
                :disabled="addContactsLoading || addContactsSaving"
                class="!m-0 !h-11 !w-full !rounded-xl !border-n-weak !bg-n-slate-2 !pl-10 !pr-3 !text-sm focus:!border-n-brand focus:!ring-1 focus:!ring-n-brand"
              />
            </div>
            <button type="submit" :disabled="addContactsLoading || addContactsSaving" class="h-11 shrink-0 rounded-xl border border-n-weak bg-n-solid-1 px-4 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-slate-3 disabled:opacity-40">Buscar</button>
          </form>

          <div class="flex items-center justify-between gap-3 text-xs">
            <span class="font-medium uppercase tracking-wide text-n-slate-10">Seus contatos</span>
            <span class="rounded-full bg-n-slate-3 px-2.5 py-1 font-medium text-n-slate-11">{{ selectedContactIds.length }} de 20 selecionados</span>
          </div>
          <div v-if="addContactsError" role="alert" class="flex items-start gap-2 rounded-xl border border-n-ruby-6 bg-n-ruby-2 p-3 text-sm text-n-ruby-11">
            <span class="i-lucide-circle-alert mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>{{ addContactsError }}</span>
          </div>

          <div class="min-h-0 max-h-[340px] overflow-y-auto rounded-xl border border-n-weak p-1">
            <label
              v-for="contact in addContacts"
              :key="contact.id"
              class="flex items-center gap-3 rounded-lg px-3 py-3 transition-colors"
              :class="selectedContactIds.includes(contact.id) ? 'bg-n-brand/10' : contactCanJoin(contact) ? 'cursor-pointer hover:bg-n-slate-2' : 'cursor-not-allowed opacity-60'"
            >
              <input
                v-model="selectedContactIds"
                type="checkbox"
                :value="contact.id"
                :disabled="addContactsSaving || !contactCanJoin(contact) || (selectedContactIds.length >= 20 && !selectedContactIds.includes(contact.id))"
                class="!m-0 size-4 shrink-0 cursor-pointer accent-n-brand disabled:cursor-not-allowed"
              />
              <Avatar :src="contact.thumbnail || contact.avatar_url" :name="contact.name" :size="40" rounded-full hide-offline-status class="shrink-0" />
              <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                <span class="truncate text-sm font-medium text-n-slate-12">{{ contact.name }}</span>
                <span class="truncate text-xs text-n-slate-10">{{ contact.phone_number || 'Sem telefone cadastrado' }}</span>
              </span>
              <span v-if="!contactCanJoin(contact)" class="shrink-0 rounded-full bg-n-slate-3 px-2 py-1 text-[11px] text-n-slate-11">{{ contactJoinUnavailableReason(contact) }}</span>
              <span v-else-if="selectedContactIds.includes(contact.id)" class="i-lucide-check size-4 shrink-0 text-n-brand" aria-hidden="true" />
            </label>
            <div v-if="!addContactsLoading && !addContacts.length" class="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <span class="i-lucide-search-x mb-1 size-8 text-n-slate-8" aria-hidden="true" />
              <span class="text-sm font-medium text-n-slate-12">Nenhum contato encontrado</span>
              <span class="text-xs text-n-slate-10">Tente outro nome ou telefone.</span>
            </div>
            <div v-if="addContactsLoading" role="status" class="flex items-center justify-center gap-2 py-8 text-sm text-n-slate-11">
              <span class="i-lucide-loader-circle size-4 animate-spin" aria-hidden="true" />
              Carregando contatos…
            </div>
          </div>
          <button v-if="addContactsHasMore" type="button" :disabled="addContactsLoading || addContactsSaving" class="self-center rounded-lg px-3 py-1.5 text-sm font-medium text-n-brand transition-colors hover:bg-n-brand/10 disabled:opacity-40" @click="loadAddContacts(addContactsPage + 1)">Carregar mais contatos</button>
          <p class="!m-0 flex items-start gap-2 text-xs leading-relaxed text-n-slate-10">
            <span class="i-lucide-info mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            Selecione até 20 contatos. Quem já participa do grupo fica indisponível.
          </p>
        </div>

        <footer class="flex flex-wrap items-center justify-between gap-3 border-t border-n-weak bg-n-slate-2 px-6 py-4">
          <button v-if="selectedContactIds.length" type="button" :disabled="addContactsSaving" class="text-xs text-n-slate-11 underline decoration-n-slate-6 underline-offset-4 hover:text-n-slate-12 disabled:opacity-40" @click="selectedContactIds = []">Limpar seleção</button>
          <span v-else class="text-xs text-n-slate-10">Escolha os contatos para continuar</span>
          <div class="ml-auto flex items-center gap-2">
            <button type="button" :disabled="addContactsSaving" class="h-10 rounded-xl border border-n-weak bg-n-solid-1 px-4 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-slate-3 disabled:opacity-40" @click="closeAddContacts">Cancelar</button>
            <button type="button" :disabled="!selectedContactIds.length || addContactsSaving || addContactsLoading" class="flex h-10 items-center gap-2 rounded-xl bg-n-brand px-4 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40" @click="confirmAddContacts">
              <span :class="addContactsSaving ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-user-round-plus'" class="size-4" aria-hidden="true" />
              {{ addContactsSaving ? 'Adicionando…' : selectedContactIds.length ? `Adicionar (${selectedContactIds.length})` : 'Adicionar' }}
            </button>
          </div>
        </footer>
      </section>
    </woot-modal>

    <woot-modal
      v-model:show="showSearchModal"
      :on-close="closeSearchModal"
      :show-close-button="false"
      class="contact-group-members-modal"
    >
      <section class="contact-group-members-modal__content">
        <header class="contact-group-members-modal__header">
          <button
            type="button"
            class="contact-group-members-modal__close"
            :aria-label="closeModalLabel"
            @click="closeSearchModal"
          >
            <span class="i-lucide-x" aria-hidden="true" />
          </button>
          <h3>{{ searchMembersLabel }}</h3>
        </header>

        <label class="contact-group-members-modal__search acl-search-pill">
          <span
            class="i-lucide-search acl-search-pill__icon"
            aria-hidden="true"
          />
          <input
            v-model="searchQuery"
            type="search"
            class="acl-search-pill__input"
            :placeholder="searchContactsPlaceholder"
            :aria-label="searchContactsPlaceholder"
          />
        </label>

        <div class="contact-group-members-modal__list">
          <button
            v-for="(participant, index) in filteredParticipants"
            :key="participantKey(participant, index)"
            type="button"
            class="contact-group-members-modal__member"
            :class="{
              'contact-group-members-modal__member--selected':
                selectedParticipant === participant,
            }"
            @click="openParticipantActions(participant)"
            @contextmenu.prevent.stop="openParticipantActions(participant)"
          >
            <Avatar
              :src="participantAvatar(participant)"
              :name="participantName(participant)"
              :size="52"
              rounded-full
              hide-offline-status
              class="contact-group-members-modal__member-avatar"
            />
            <span class="contact-group-members-modal__member-details">
              <span class="contact-group-members-modal__member-name">
                {{ participantName(participant) }}
              </span>
              <span
                v-if="participantSecondaryText(participant)"
                class="contact-group-members-modal__member-secondary"
              >
                {{ participantSecondaryText(participant) }}
              </span>
            </span>
            <span
              v-if="participantRoleBadge(participant)"
              class="contact-group-members-modal__member-role"
            >
              {{ participantRoleBadge(participant) }}
            </span>
          </button>
          <p
            v-if="!filteredParticipants.length"
            class="contact-group-members-modal__empty"
          >
            {{ noMembersFoundLabel }}
          </p>
        </div>
      </section>
    </woot-modal>

    <woot-modal
      v-model:show="showGroupToolsModal"
      :show-close-button="false"
      class="contact-group-member-modal"
    >
      <section class="contact-group-member-modal__content">
        <header class="contact-group-member-modal__header">
          <h3>{{ groupToolsLabel }}</h3>
          <button
            type="button"
            class="contact-group-member-modal__close"
            :aria-label="closeModalLabel"
            @click="showGroupToolsModal = false"
          >
            <span class="i-lucide-x" aria-hidden="true" />
          </button>
        </header>
        <div class="contact-group-member-modal__actions">
          <button type="button" @click="updateGroupSetting('announcement')">
            <span class="i-lucide-megaphone" aria-hidden="true" />
            <span>Somente admins enviam mensagens</span>
          </button>
          <button type="button" @click="updateGroupSetting('not_announcement')">
            <span class="i-lucide-message-circle" aria-hidden="true" />
            <span>Todos podem enviar mensagens</span>
          </button>
          <button type="button" @click="updateGroupSetting('locked')">
            <span class="i-lucide-lock" aria-hidden="true" />
            <span>Somente admins alteram dados</span>
          </button>
          <button type="button" @click="updateGroupSetting('unlocked')">
            <span class="i-lucide-unlock" aria-hidden="true" />
            <span>Todos podem alterar dados</span>
          </button>
          <button type="button" @click="copyInviteLink">
            <span class="i-lucide-link" aria-hidden="true" />
            <span>Copiar link de convite</span>
          </button>
        </div>
      </section>
    </woot-modal>

    <woot-modal
      v-model:show="showParticipantModal"
      :on-close="closeParticipantActions"
      :show-close-button="false"
      class="contact-group-member-modal"
    >
      <section
        v-if="selectedParticipant"
        class="contact-group-member-modal__content"
      >
        <header class="contact-group-member-modal__header">
          <button
            type="button"
            class="contact-group-member-modal__close"
            :aria-label="closeModalLabel"
            @click="closeParticipantActions"
          >
            <span class="i-lucide-x" aria-hidden="true" />
          </button>
          <Avatar
            :src="participantAvatar(selectedParticipant)"
            :name="selectedParticipantName"
            :size="64"
            rounded-full
            hide-offline-status
          />
          <div class="contact-group-member-modal__identity">
            <h3>{{ selectedParticipantName }}</h3>
            <span v-if="participantSecondaryText(selectedParticipant)">
              {{ participantSecondaryText(selectedParticipant) }}
            </span>
          </div>
        </header>

        <dl
          v-if="showParticipantDetails"
          class="grid gap-3 px-6 pb-4 text-sm text-n-slate-12"
        >
          <div>
            <dt class="text-n-slate-11">{{ participantDetailLabels.name }}</dt>
            <dd class="m-0">{{ selectedParticipantName }}</dd>
          </div>
          <div>
            <dt class="text-n-slate-11">{{ participantDetailLabels.phone }}</dt>
            <dd class="m-0">
              {{ selectedParticipantPhone || participantDetailLabels.noPhone }}
            </dd>
          </div>
          <div>
            <dt class="text-n-slate-11">{{ participantDetailLabels.role }}</dt>
            <dd class="m-0">
              {{
                participantRoleBadge(selectedParticipant) ||
                participantDetailLabels.member
              }}
            </dd>
          </div>
        </dl>
        <p
          v-if="showParticipantDetails"
          class="m-0 px-6 pb-4 text-sm text-n-slate-11"
        >
          {{ participantDetailLabels.noRegistration }}
        </p>

        <div class="contact-group-member-modal__actions">
          <button
            v-if="canManageSelectedParticipant && !selectedParticipantIsAdmin"
            type="button"
            @click="updateSelectedParticipant('promote')"
          >
            <span class="i-lucide-user-round-cog" aria-hidden="true" />
            <span>{{ promoteAdminLabel }}</span>
          </button>
          <button
            v-if="canManageSelectedParticipant && selectedParticipantIsAdmin && selectedParticipant.role !== 'owner'"
            type="button"
            @click="updateSelectedParticipant('demote')"
          >
            <span class="i-lucide-user-round-x" aria-hidden="true" />
            <span>{{ demoteAdminLabel }}</span>
          </button>
          <button
            v-if="canManageSelectedParticipant"
            type="button"
            @click="updateSelectedParticipant('remove')"
          >
            <span class="i-lucide-circle-minus" aria-hidden="true" />
            <span>{{ removeMemberLabel }}</span>
          </button>
          <button
            v-if="!showParticipantDetails"
            type="button"
            :disabled="isOpeningParticipant"
            @click="openSelectedParticipantDetails"
          >
            <span class="i-lucide-circle-user-round" aria-hidden="true" />
            <span>{{ contactDetailsLabel }}</span>
          </button>
          <button
            type="button"
            :disabled="isOpeningParticipant"
            @click="startSelectedParticipantConversation"
          >
            <span class="i-lucide-message-circle" aria-hidden="true" />
            <span>{{ `Conversar com ${selectedParticipantName}` }}</span>
          </button>
        </div>
      </section>
    </woot-modal>
  </section>
</template>

<style scoped>
.contact-details-group {
  width: 100%;
  overflow: hidden;
  background: #fff;
  color: #111827;
}

.contact-details-group__header {
  display: flex;
  min-height: 54px;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px 0 30px;
  background: #fff;
}

.contact-details-group__title {
  margin: 0;
  color: #1f2937;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25;
}

.contact-details-group__search {
  display: inline-flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border: 0;
  /* O recheio que o navegador poe de fabrica em <button> (1px 6px no Chrome)
     nao soma a largura — ela esta fixa em 36px —, tira-a de dentro: sobravam
     24px de caixa de conteudo para o icone. Com o icone em 20px ninguem
     reparava; a 24px ele ficaria encostado nos dois lados. */
  padding: 0;
  border-radius: 9999px;
  background: transparent;
  color: #111827;
  cursor: pointer;
}

.contact-details-group__search:hover,
.contact-details-group__search:focus-visible {
  background: #f1f5f9;
  outline: none;
}

/* 24px e a medida do icone de cabecalho do WhatsApp. Os 20px anteriores
   pareciam apagados ao lado do titulo do bloco. */
.contact-details-group__search > span {
  width: 24px;
  height: 24px;
}

.contact-details-group__content {
  padding: 4px 20px 12px 30px;
  font-size: 0.95rem;
}

.contact-details-group__member {
  display: flex;
  min-height: 76px;
  align-items: center;
  gap: 16px;
  margin: 0;
  padding: 10px 16px;
  border: 0;
  background: transparent;
  cursor: pointer;
  outline: 1px solid transparent;
  text-align: left;
  transition:
    background-color 160ms ease,
    outline-color 160ms ease;
}

.contact-details-group__member-avatar {
  flex-shrink: 0;
}

.contact-details-group__member-text {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 3px;
}

.contact-details-group__member-name,
.contact-details-group__member-phone {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-details-group__member-name {
  color: #1f2937;
  font-size: 0.95rem;
  font-weight: 600;
}

.contact-details-group__member-phone {
  color: #7a7f86;
  font-size: 0.825rem;
}

.contact-details-group__member-role {
  flex-shrink: 0;
  padding: 5px 8px;
  border-radius: 6px;
  background: #d9fbdc;
  color: #18864b;
  font-size: 0.6875rem;
  font-weight: 700;
}

.contact-details-group__empty {
  margin: 0;
  padding: 18px 0;
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
}

.contact-group-members-modal__content {
  box-sizing: border-box;
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  padding: 28px 32px 32px;
  border-radius: 24px;
  background: #fff;
  color: #111827;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
}

.contact-group-members-modal__header {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 22px;
  height: auto;
  padding: 0;
  margin-bottom: 20px;
}

.contact-group-members-modal__header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.3;
}

.contact-group-members-modal__close {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
  font-size: 28px;
}

.contact-group-members-modal__close:hover,
.contact-group-members-modal__close:focus-visible {
  background: transparent;
  outline: none;
}

.contact-group-members-modal__close > span {
  width: 28px;
  height: 28px;
}

.contact-group-members-modal__search {
  display: flex;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 8px;
  margin-bottom: 28px;
  padding: 5px 14px;
  border: none;
  border-radius: 50px;
  background: rgba(194, 189, 184, 0.15);
  color: #8a8a8a;
  box-shadow: inset 0 0 0 0 transparent;
  transition: box-shadow 0.12s;
}

.contact-group-members-modal__search:hover {
  box-shadow: inset 0 0 0 1px #c4c4c4;
}

.contact-group-members-modal__search:focus-within {
  box-shadow: inset 0 0 0 1.5px #1b8755;
}

.contact-group-members-modal__search .acl-search-pill__icon {
  flex-shrink: 0;
  color: #8a8a8a;
}

.contact-group-members-modal__search input {
  box-sizing: border-box;
  min-width: 0;
  flex: 1;
  width: 100%;
  height: 40px;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #1f2937;
  font-size: 13px;
  line-height: 40px;
  box-shadow: none;
}

.contact-group-members-modal__search input::placeholder {
  color: #8a8a8a;
}

.contact-group-members-modal__search input:focus,
.contact-group-members-modal__search input:focus-visible {
  border: none;
  outline: none;
  background: transparent;
  box-shadow: none;
}

.contact-group-members-modal__list {
  width: 100%;
  min-width: 0;
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
}

.contact-group-members-modal__list::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.contact-group-members-modal__member {
  display: flex;
  box-sizing: border-box;
  min-height: 82px;
  min-width: 0;
  max-width: 100%;
  align-items: center;
  gap: 18px;
  width: 100%;
  padding: 8px 4px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #111827;
  cursor: pointer;
  text-align: left;
}

.contact-group-members-modal__member:hover,
.contact-group-members-modal__member--selected {
  background: #f8fafc;
}

.contact-group-members-modal__member-details {
  display: flex;
  overflow: hidden;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}

.contact-group-members-modal__member-name,
.contact-group-members-modal__member-secondary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-group-members-modal__member-name {
  color: #1f2937;
  font-size: 1.05rem;
  font-weight: 500;
  line-height: 1.35;
}

.contact-group-members-modal__member-secondary {
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.35;
}

.contact-group-members-modal__member-role {
  max-width: 35%;
  overflow: hidden;
  flex-shrink: 0;
  padding: 6px 10px;
  border-radius: 9999px;
  background: #d9fbdc;
  color: #18864b;
  font-size: 0.75rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-group-members-modal__empty {
  margin: 24px 0;
  color: #64748b;
  text-align: center;
}

.contact-group-member-modal__content {
  width: min(420px, calc(100vw - 32px));
  padding: 24px 0 12px;
  border-radius: 18px;
  background: #fff;
  color: #111827;
}

.contact-group-member-modal__header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 24px 24px;
}

.contact-group-member-modal__close {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  margin-right: -4px;
  border: 0;
  background: transparent;
  color: #111827;
  cursor: pointer;
}

.contact-group-member-modal__identity {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.contact-group-member-modal__identity h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
}

.contact-group-member-modal__identity span {
  overflow: hidden;
  color: #64748b;
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contact-group-member-modal__actions {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 8px 0 0;
  border-top: 1px solid #e2e8f0;
}

.contact-group-member-modal__actions button {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 54px;
  padding: 0 24px;
  border: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  font-size: 1rem;
  text-align: left;
}

.contact-group-member-modal__actions button:hover {
  background: #f8fafc;
}

.contact-group-member-modal__actions button > span:first-child {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

:global(.modal-mask.contact-group-members-modal),
:global(.modal-mask:has(.contact-group-member-modal)) {
  background: rgba(15, 23, 42, 0.55) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

:global(.modal-mask.contact-group-members-modal .modal-container),
:global(.modal-mask:has(.contact-group-member-modal) .modal-container) {
  background: #fff !important;
}

:global(.modal-mask.contact-group-members-modal) {
  overflow: hidden !important;
  padding: 16px !important;
}

:global(.modal-mask.contact-group-members-modal .modal-container) {
  width: min(656px, calc(100vw - 32px)) !important;
  max-width: calc(100vw - 32px) !important;
  height: min(760px, calc(100vh - 32px)) !important;
  max-height: calc(100vh - 32px) !important;
  overflow: hidden !important;
  border-radius: 2rem !important;
  background: #fff !important;
  padding: 0 !important;
}

@media (max-width: 640px) {
  .contact-group-members-modal__content {
    width: calc(100vw - 2rem);
    max-width: calc(100vw - 2rem);
    height: calc(100vh - 2rem);
    max-height: calc(100vh - 2rem);
    padding: 20px 16px 24px;
    border-radius: 1.5rem;
  }

  .contact-group-members-modal__header {
    gap: 12px;
    margin-bottom: 16px;
  }

  .contact-group-members-modal__header h3 {
    overflow: hidden;
    font-size: 1.125rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .contact-group-members-modal__search {
    margin-bottom: 16px;
  }

  .contact-group-members-modal__member {
    min-height: 76px;
    gap: 12px;
    padding: 8px;
  }

  .contact-group-members-modal__member-name {
    font-size: 1rem;
  }

  .contact-group-members-modal__member-secondary {
    font-size: 0.875rem;
  }

  .contact-group-members-modal__member-role {
    max-width: 38%;
    padding: 5px 8px;
    font-size: 0.75rem;
  }
}
</style>
